import type * as babylon from "@babylonjs/core";

/**
 * A point pick result: which point, and where along the ray it sits.
 * Distances are in the frame the points and ray were expressed in.
 */
export interface PointRayHit {
  /** Index of the point among the points searched, not of its first coordinate. */
  index: number;
  /** Depth: distance along the ray to the point's perpendicular foot. */
  t: number;
  /** Perpendicular distance from the point to the ray. */
  distanceToRay: number;
}

/**
 * The point a pick ray "means" among a flat vertex buffer, or `undefined`
 * when the ray misses.
 *
 * A ray never passes exactly through a point, so a hit is any point inside a
 * cone around the ray: perpendicular distance ≤ `tanHalfAngle · depth`. The
 * cone widens with depth, making the tolerance a screen-space radius; see
 * {@link angularToleranceFor} for deriving `tanHalfAngle` from pixels.
 *
 * Among points inside the cone, the shallowest wins — a pick means "the
 * visible thing under the cursor". Equal depths go to the point nearer the
 * axis. Points behind the origin are never hits, and neither are non-finite
 * ones: glTF permits NaN in a POSITION accessor, and such a vertex would
 * otherwise win every pick.
 *
 * Positions and the ray must be in the same frame, and `direction` must be
 * normalized; `t` and `distanceToRay` are in that frame's units. Brute
 * force, O(n): call at most once per rendered frame, never per pointer
 * event.
 */
export function nearestPointToRay(
  positions: ArrayLike<number>,
  ray: { origin: babylon.Vector3; direction: babylon.Vector3 },
  tanHalfAngle: number,
): PointRayHit | undefined {
  const { origin, direction } = ray;
  let best: PointRayHit | undefined;

  for (let i = 0; i + 2 < positions.length; i += 3) {
    best = closerOf(
      best,
      i / 3,
      positions[i] - origin.x,
      positions[i + 1] - origin.y,
      positions[i + 2] - origin.z,
      direction,
      tanHalfAngle,
    );
  }

  return best;
}

/**
 * {@link nearestPointToRay} over points already materialized as vectors, for
 * the trajectory samples a path trace is drawn from. Same acceptance rules,
 * same units; `index` indexes the array directly, so a parallel array of
 * sample times can be read at the hit.
 *
 * Brute force, O(n), like its sibling.
 */
export function nearestVectorToRay(
  points: readonly babylon.Vector3[],
  ray: { origin: babylon.Vector3; direction: babylon.Vector3 },
  tanHalfAngle: number,
): PointRayHit | undefined {
  const { origin, direction } = ray;
  let best: PointRayHit | undefined;

  for (let index = 0; index < points.length; index += 1) {
    const point = points[index];
    best = closerOf(
      best,
      index,
      point.x - origin.x,
      point.y - origin.y,
      point.z - origin.z,
      direction,
      tanHalfAngle,
    );
  }

  return best;
}

/**
 * Both searches' inner step, and the single copy of the acceptance rules:
 * the candidate at `index`, offset `(vx, vy, vz)` from the ray origin, either
 * beats `best` or leaves it standing.
 *
 * Coordinates arrive as scalars so a flat vertex buffer and a `Vector3[]`
 * share this without either paying for the other's layout.
 */
function closerOf(
  best: PointRayHit | undefined,
  index: number,
  vx: number,
  vy: number,
  vz: number,
  direction: babylon.Vector3,
  tanHalfAngle: number,
): PointRayHit | undefined {
  const t = vx * direction.x + vy * direction.y + vz * direction.z;
  // `!(t > 0)` rather than `t <= 0` so a NaN `t` (non-finite point) is
  // rejected: comparisons against NaN are all false, so `t <= 0` would
  // wave it through every later guard to become a phantom `best`.
  if (!(t > 0)) {
    return best;
  }
  // A deeper point loses regardless of offset; skip the cone test.
  if (best !== undefined && t > best.t) {
    return best;
  }
  const perpendicularSq = vx * vx + vy * vy + vz * vz - t * t;
  const radius = tanHalfAngle * t;
  if (perpendicularSq > radius * radius) {
    return best;
  }
  // Cancellation can push perpendicularSq a hair below zero for a
  // near-axis point; clamp so a dead-center hit reports 0, not NaN.
  const distanceToRay = Math.sqrt(Math.max(perpendicularSq, 0));
  if (
    best !== undefined &&
    t === best.t &&
    distanceToRay >= best.distanceToRay
  ) {
    return best;
  }
  return { index, t, distanceToRay };
}

/**
 * The `tanHalfAngle` a pick radius of `radiusPx` subtends, for a perspective
 * camera with vertical field of view `fovYRad` rendering `viewportHeightPx`
 * pixels: at depth 1 the viewport spans `2 · tan(fovY / 2)` world units over
 * `viewportHeightPx` pixels, and a pixel radius scales by that ratio.
 *
 * The conversion is exact only at the view center. A cursor θ off the view
 * axis picks with an effective radius of up to `radiusPx / cos²θ` — the
 * tolerance loosens toward the corners, so the cursor gets grabbier there,
 * never unpickable.
 */
export function angularToleranceFor(
  radiusPx: number,
  viewportHeightPx: number,
  fovYRad: number,
): number {
  return (2 * radiusPx * Math.tan(fovYRad / 2)) / viewportHeightPx;
}
